import { makeStyles } from '@material-ui/styles'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { customValidator, nameWithHyphenAndUnderscoreValidator } from 'core/utils/fieldValidators'
import { trimInputOnBlur } from 'core/utils/helpers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { convertToTitleCase, GET_DRIVER_CONFIG, getPairValue, waitSeconds } from '../helpers'
import BackendDriverPicklist from './BackendDriverPicklist'
import Alert from 'core/components/Alert'
import { VolumeBackend } from '../ClusterBlueprintPage'

const useStyles = makeStyles<Theme>((theme) => ({
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
}))

interface Props {
  volumeConfig: any
  currentVolumeConfigs: any[]
  updateVolumeConfigs: (config: any) => void
  onClose: () => void
  volumeBackend: VolumeBackend
  matchingBackendConfigs: any[]
}

const PAIR_TO_REMOVE = ['volume_backend_name']

const DRIVERS = [
  'NFS',
  'LVM',
  'NetAppONTAPNFS',
  'NetAppONTAPFC',
  'NetAppONTAPISCSI',
  'NetAppONTAPNVMe',
  'PureStorageISCSI',
  'PureStorageFC',
  'PureStorageNVME',
  'HitachiISCSI',
  'HitachiFC',
  'InfinidatISCSI',
  'HuaweiISCSI',
  'HuaweiFC',
  'Custom',
  'HPE3PARISCSI',
  'HPE3PARFC',
  'Tintri',
]

const ERROR_MESSAGE = (driver) => ({
  title: 'Driver not supported for production',
  message: `${driver} storage driver is not supported for production use. 
    Use this driver only for testing or evaluation. 
    For production, please select a supported storage driver`,
})

const KEYVALUE_UPDATE_DELAY = 0.01

export default function CreateVolumeBackendConfigurationModal({
  volumeConfig,
  currentVolumeConfigs,
  updateVolumeConfigs,
  onClose,
  volumeBackend,
  matchingBackendConfigs,
}: Props) {
  // If there is a driver already chosen for the volume backend
  // then we should restrict it to the same driver
  const driverForBackend = matchingBackendConfigs?.[0]?.driver || ''

  const [showKeyValues, setShowKeyValues] = useState(true)
  const [defaultDriverKeyValues, setDefaultDriverKeyValues] = useState([])
  const [error, setError] = useState(null)

  const requiredKeyValueField = useMemo(
    () =>
      volumeConfig?.driver
        ? GET_DRIVER_CONFIG[volumeConfig?.driver].keyValues
            ?.filter((item) => item.required)
            ?.map((item) => item.key)
        : [],
    [volumeConfig?.driver],
  )

  const defaultParams = useMemo(
    () =>
      volumeConfig
        ? {
            name: volumeConfig.name,
            driver: volumeConfig.driver,
            // Because create vs update is different, for july need to
            // disable the removal of those required keys during update
            keyValues: volumeConfig.keyValues?.filter(
              (pair) => !requiredKeyValueField?.includes(pair.key),
            ),
            customDriver: volumeConfig.customDriver,
          }
        : {
            name: '',
            driver: driverForBackend,
            keyValues: [],
            customDriver: '',
          },
    [volumeConfig?.keyValues, requiredKeyValueField, driverForBackend],
  )

  const classes = useStyles()
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  // When editing an existing configuration, populate the form with saved values:
  // - Required fields go to Basic Settings section (defaultDriverKeyValues)
  // - Optional fields + custom user-added fields go to Optional Key Value Configurations (params.keyValues)
  useEffect(() => {
    if (volumeConfig?.driver && GET_DRIVER_CONFIG[volumeConfig.driver]) {
      const driverConfig = GET_DRIVER_CONFIG[volumeConfig.driver].keyValues

      // Separate required and optional fields from driver config
      const requiredConfigFields = driverConfig.filter((item) => item?.required)
      const optionalConfigFields = driverConfig.filter((item) => !item?.required)

      // Merge existing values with required driver config fields
      const mergedRequiredFields = requiredConfigFields.map((configItem) => {
        const existingValue = volumeConfig.keyValues?.find((kv) => kv.key === configItem.key)
        return {
          ...configItem,
          value: existingValue?.value ?? configItem.value,
        }
      })
      setDefaultDriverKeyValues(mergedRequiredFields)

      // Merge existing values with optional driver config fields for KeyValuesField
      const mergedOptionalFields = optionalConfigFields.map((configItem) => {
        const existingValue = volumeConfig.keyValues?.find((kv) => kv.key === configItem.key)
        return {
          key: configItem.key,
          value: existingValue?.value ?? configItem.value,
        }
      })

      // Also include any custom key-values that user added (not in driver config)
      const driverConfigKeys = driverConfig?.map((item) => item.key)
      const requiredConfigKeys = requiredConfigFields?.map((item) => item.key)
      const customKeyValues =
        volumeConfig.keyValues?.filter(
          (kv) => ![...driverConfigKeys, ...requiredConfigKeys].includes(kv.key),
        ) || []

      getParamsUpdater('keyValues')([...mergedOptionalFields, ...customKeyValues])
    }
  }, [volumeConfig])

  const updateKeyValuePair = async (value) => {
    setShowKeyValues(false)
    await waitSeconds(KEYVALUE_UPDATE_DELAY)
    getParamsUpdater('keyValues')(value)
    setShowKeyValues(true)
  }

  const onKeyValueChange = async (value) => {
    let newValue = value
    const hasNfsMountPoint = value.some((pair) => pair.key === 'nfs_mount_points')

    if (params.driver === 'NetApp') {
      if (value.some((pair) => pair.key === 'netapp_storage_protocol' && pair.value === 'nfs')) {
        if (!hasNfsMountPoint) {
          newValue = [...value, { key: 'nfs_mount_points', value: '', shouldNotShowDelete: true }]
          updateKeyValuePair(newValue)
        }
      }
      if (value.some((pair) => pair.key === 'netapp_storage_protocol' && pair.value !== 'nfs')) {
        if (hasNfsMountPoint) {
          newValue = value.filter((pair) => pair.key !== 'nfs_mount_points')
          updateKeyValuePair(newValue)
        }
      }
    }

    if (value.some((pair) => PAIR_TO_REMOVE.includes(pair.key) && pair.value === '')) {
      newValue = value.map((pair) => {
        if (PAIR_TO_REMOVE.includes(pair.key))
          return {
            key: pair.key,
            value: getPairValue(params.name)[pair.key],
            disabledValue: true,
            disabledKey: true,
          }
        return pair
      })
      updateKeyValuePair(newValue)
      return
    }

    getParamsUpdater('keyValues')(newValue)
  }

  const selectedDriver = (driver) => {
    if (!DRIVERS.includes(driver)) {
      return
    }

    const runCheck = async () => {
      if (!!GET_DRIVER_CONFIG[driver]) {
        setShowKeyValues(false)
        await waitSeconds(0.5)
        const driverConfig = GET_DRIVER_CONFIG[driver].keyValues
        // Separate required and optional fields
        const requiredFields = driverConfig.filter((item) => item?.required)
        const optionalFields = driverConfig.filter((item) => !item?.required)

        // Set required fields to defaultDriverKeyValues (shown in Basic Settings)
        setDefaultDriverKeyValues(requiredFields)

        // Set optional fields to params.keyValues (shown in Optional Key Value Configurations)
        const optionalKeyValues = optionalFields.map((item) => ({
          key: item.key,
          value: item.value,
        }))
        getParamsUpdater('keyValues')(optionalKeyValues)
        setShowKeyValues(true)
      }
    }
    runCheck()
  }

  const duplicateNameValidator = useMemo(() => {
    return customValidator((value) => {
      if (!value) {
        return true
      }
      if (volumeConfig?.name === value) {
        return true
      }
      return !currentVolumeConfigs.find((config) => config?.name === value)
    }, 'A configuration with that name already exists')
  }, [currentVolumeConfigs])

  const nfsSharesConfigValidator = useMemo(() => {
    return customValidator((value) => {
      if (!value) {
        return true
      }
      // Check for other NFS drivers, if they're on a different volume type
      // then make sure the nfs_shares_config is different
      const nfsConfigsOnOtherVolumeTypes = currentVolumeConfigs?.filter(
        (config) =>
          config?.volumeBackendName !== volumeBackend?.type &&
          ['NFS', 'NetAppONTAPNFS'].includes(config?.driver),
      )
      return !nfsConfigsOnOtherVolumeTypes?.find((config) => {
        const pair = config?.keyValues?.find((keyValue) => keyValue?.key === 'nfs_shares_config')
        return pair?.value === value
      })
    }, 'This NFS share config is already in-use by another volume type')
  }, [currentVolumeConfigs, volumeBackend?.type])

  // useEffect(() => {
  //   if (volumeConfig) {
  //     updateParams({
  //       ...volumeConfig,
  //     })
  //   }
  // }, [volumeConfig])

  const submitForm = useCallback(async () => {
    const newKeyValuePairs = params.keyValues.filter((pair) => !PAIR_TO_REMOVE.includes(pair.key))
    const keyValuePairs = [...defaultDriverKeyValues, ...newKeyValuePairs]

    const seen = new Set()
    const duplicateKey = keyValuePairs?.find((pair) => {
      if (seen.has(pair?.key)) return true
      seen.add(pair?.key)
      return false
    })?.key

    if (!!duplicateKey) {
      setError({
        title: 'May not have a duplicate key',
        message: `Key ${duplicateKey} is duplicate.`,
      })
      return
    } else {
      setError(null)
    }

    const config = {
      name: params.name,
      driver: params.driver,
      keyValues: keyValuePairs,
      customDriver: params.customDriver,
      volumeBackendName: volumeBackend.type,
      volumeBackendId: volumeBackend.id,
    }
    updateVolumeConfigs(config)
    handleClose()
  }, [params, volumeBackend, defaultDriverKeyValues])

  const handleClose = () => {
    setParams(defaultParams)
    onClose()
  }

  const updateDriverFieldValue = useCallback(
    (key: string, value: string) => {
      const updatedKeyValues = defaultDriverKeyValues?.map((kvItem) => {
        if (kvItem?.key === key) {
          return { ...kvItem, value: value }
        }
        return { ...kvItem }
      })
      setDefaultDriverKeyValues(updatedKeyValues)
    },
    [defaultDriverKeyValues],
  )

  const isEditMode = !!volumeConfig

  return (
    <ModalForm
      title={`${isEditMode ? 'Edit' : 'Add'} Volume Backend Configuration`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitTitle={isEditMode ? 'Save Changes' : 'Add Volume Backend Configuration'}
      open
      error={error}
    >
      <FormFieldSection title="Basic Settings" alignContentLeft>
        {/* Name and Storage Driver in two-column layout */}
        <div className={classes.twoColumnGrid}>
          <PicklistField
            DropdownComponent={BackendDriverPicklist}
            name="driver"
            id="driver"
            label="Storage Driver"
            compact={false}
            value={params.driver}
            onChange={(value) => {
              getParamsUpdater('driver')(value)
              selectedDriver(value)
            }}
            disabled={isEditMode || !!driverForBackend}
            info="The storage driver for a volume backend must be the same across all configurations"
            required
          />
          <TextField
            id="name"
            label="Configuration Name"
            onChange={getParamsUpdater('name')}
            onBlur={(e) => trimInputOnBlur(e, getParamsUpdater('name'))}
            value={params.name}
            validations={[nameWithHyphenAndUnderscoreValidator, duplicateNameValidator]}
            required
          />
        </div>
        {['LVM', 'NFS'].includes(params.driver) && (
          <Alert
            variant="warning"
            title={ERROR_MESSAGE(params.driver)?.title}
            message={ERROR_MESSAGE(params.driver)?.message}
          />
        )}
        {params.driver === 'Custom' && (
          <TextField
            id="customDriver"
            label="Custom Driver Name"
            onChange={getParamsUpdater('customDriver')}
            onBlur={(e) => trimInputOnBlur(e, getParamsUpdater('customDriver'))}
            value={params.customDriver}
            info="Must be a fully qualified driver name"
            required
          />
        )}
        {/* Required Key Value Fields - shown in two-column grid */}
        {defaultDriverKeyValues.length > 0 && (
          <div className={classes.twoColumnGrid}>
            {defaultDriverKeyValues.map((item) => (
              <TextField
                key={item?.key}
                id={item?.key}
                label={convertToTitleCase(item?.key)}
                required
                value={item?.value}
                placeholder="Value"
                onChange={(value) => updateDriverFieldValue(item?.key, value)}
                validations={item?.key === 'nfs_shares_config' ? [nfsSharesConfigValidator] : null}
                info={item?.tooltip}
                align={{ vertical: 'top', horizontal: 'middle' }}
                disabled={item?.disabled}
              />
            ))}
          </div>
        )}
      </FormFieldSection>

      {/* Optional Key Value Configurations Section - only show when driver is selected */}
      {params.driver && showKeyValues && (
        <FormFieldSection title="Optional Key Value Configurations" alignContentLeft>
          <KeyValuesField
            id="keyValues"
            label="Key Values"
            value={params.keyValues}
            onChange={onKeyValueChange}
            addLabel="Add Key/Value Pair"
            allowMultipleValues
            fullWidth
          />
        </FormFieldSection>
      )}
    </ModalForm>
  )
}
